import AlertingConditionsSection from "./AlertingConditionsSection";
import { CustomizeSignalSection } from "./CustomizeSignalSection";
import { SelectDataSection } from "./SelectDataSection";
import { Button, SmartForm } from "@avenueops/web-components";
import { OvalLoader } from "@avenueops/web-components";
import { ArrowDownTrayIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronLeftIcon, PencilIcon } from "@heroicons/react/24/outline";
import GeneralModal from "components/GeneralModal";
import EmojiPicker from "components/inputs/EmojiPicker";
import { useQueryColumns } from "context/QueryRunContext";
import { debounce } from "lodash";
import moment from "moment";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import useDeepCompareEffect from "use-deep-compare-effect";
import { pagesPath } from "utils/$path";

interface Props {
  saveDraft?: (data: Record<string, any>) => Promise<void>;
  backTo: string;
}

export function CreatePage({ saveDraft, backTo }: Props) {
  const [isExpandedView, setIsExpandedView] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const { watch, control, formState } = useFormContext();
  const emoji = watch("emoji");
  const title = watch("title");
  const type = watch("_workflowType");
  const workflowTrigger = watch("workflowTriggerType");
  const router = useRouter();
  const { isDirty } = formState;

  const watchedData = useWatch({
    control: control,
  });

  const [lastSave, setLastSave] = useState<Date | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const debouncedSave = useCallback(
    debounce(async () => {
      if (saveDraft) {
        await saveDraft(watch());
        setLastSave(new Date());
        setSaving(false);
      }
    }, 1000),
    [],
  );

  useDeepCompareEffect(() => {
    if (isDirty) {
      setSaving(true);
      debouncedSave();
    }
  }, [watchedData]);

  const getWorkflowTypeCopy = (type: string) => {
    switch (type) {
      case "WEBHOOK":
        return "Webhook monitors trigger alerts when a request is made to the address below. Alerts contain all relevant information to your business event.";
      case "FORM":
        return "Form monitors trigger alerts when the selected form is filled out. Alerts contain all relevant information to your business event.";
      case "REPORT":
        return "Report monitors trigger alerts at a set schedule. Alerts contain all relevant information to your business event.";
      case "METRIC":
        return "Metric monitors trigger alerts when a metric is breached. Alerts contain all relevant information to your business event.";
      case "EVENT":
        return "Event monitors trigger alerts whens your data changes. Alerts contain all relevant information to your business event.";
      case "REACTION_ADDED":
        return "This monitor triggers an alert when a thread in the channels specified on the left are reacted to with the 🎫 (ticket) emoji. Alerts contain all relevant information to your business event.";
      case "NEW_MESSAGE":
        return "This monitor triggers an alert when a new message is sent in the channels specified on the left. Alerts contain all relevant information to your business event.";
    }
  };
  const queryColumns = useQueryColumns();
  const idColumnsMatchPreview = !watchedData.idColumns?.find((idColumn: string) => !queryColumns.includes(idColumn));

  return (
    <>
      <div className="w-screen h-screen flex flex-col">
        <div className="h-24 flex overflow-visible">
          <div className="flex-1 flex flex-row">
            <div className="flex-1 flex flex-row items-center justify-between bg-gray-100 h-full">
              <div className="flex items-center gap-2">
                <div onClick={() => router.push(backTo)} className="p-2 ml-6 cursor-pointer rounded-xl hover:bg-gray-200 flex flex-row">
                  <ChevronLeftIcon className="h-6 w-6" />
                </div>
                {isExpandedView ? (
                  <div className="text-gray-900 text-xl font-semibold select-none">
                    {emoji} {title ?? "Monitor"}
                  </div>
                ) : null}
              </div>
              {!isExpandedView ? (
                <div className="items-center p-2 mr-4 cursor-pointer rounded-xl h-fit hover:bg-gray-200" onClick={() => setIsExpandedView(true)}>
                  <ChevronDoubleRightIcon className="w-6 h-6" />
                </div>
              ) : null}
            </div>
            <div className="flex-1 bg-white data-[expanded=true]:bg-gray-100 w-full" data-expanded={isExpandedView}>
              <div className="flex justify-between data-[expanded=true]:justify-end m-6 items-center gap-10" data-expanded={isExpandedView}>
                <div className="relative flex-1 h-12 max-w-full">
                  {isExpandedView ? null : (
                    <div className="flex flex-row gap-4">
                      <EmojiPicker
                        hideLabel
                        name="emoji"
                        control={control}
                        trigger={<div className="bg-white cursor-pointer px-4 py-3 text-xl rounded-xl flex items-center w-fit shadow">{emoji}</div>}
                      />
                      <h1 className="z-20 text-xl font-semibold text-gray-800 whitespace-nowrap flex flex-1 items-center gap-1">
                        <SmartForm.TextInputInline placeholder="Title" name="title" defaultValue={title} />
                      </h1>
                    </div>
                  )}
                </div>
                <div className="flex gap-4 items-center">
                  {saveDraft ? (
                    saving ? (
                      <div className="flex gap-1 items-center">
                        <div className="text-gray-600 text-sm">Saving</div>
                        <OvalLoader type="DEFAULT" size={12} />
                      </div>
                    ) : lastSave ? (
                      <div className="flex flex-1 gap-1 items-center">
                        <div className="text-gray-600 text-sm lg:inline-flex hidden">{`Last saved ${moment
                          .duration(new Date().getTime() - lastSave.getTime())
                          .humanize()} ago`}</div>
                        <ArrowDownTrayIcon className="text-gray-600 h-4 w-4 shrink-0 cursor-pointer" />
                      </div>
                    ) : null
                  ) : null}
                  {saveDraft && !lastSave ? (
                    <Button
                      type="SECONDARY"
                      onClick={() => {
                        router.push(pagesPath.monitors.$url());
                        saveDraft(watch());
                      }}
                    >
                      Save as Draft
                    </Button>
                  ) : null}
                  {!idColumnsMatchPreview ? <Button onClick={() => setModalVisible(true)}>Save</Button> : <Button submit>Save</Button>}
                </div>
              </div>
            </div>
            {isExpandedView ? (
              <div className="w-20 bg-white pt-5 flex justify-center cursor-pointer ">
                <div className="h-fit p-2 hover:bg-gray-200 rounded-xl flex items-center" onClick={() => setIsExpandedView(false)}>
                  <ChevronDoubleLeftIcon className="w-6 h-6" />
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex-1 flex overflow-hidden">
          <div className="w-[50%] data-[expanded=true]:flex-1 bg-gray-100 overflow-scroll pb-20" data-expanded={isExpandedView}>
            <SelectDataSection />
          </div>
          <div className="data-[expanded=true]:w-20 w-[50%] bg-white overflow-scroll" data-expanded={isExpandedView}>
            <div className="px-5 mx-6 border-t border-gray-200 data-[expanded=true]:hidden" data-expanded={isExpandedView}>
              <AlertingConditionsSection monitor={undefined} />
              <div className="pt-5">
                <div className="text-lg font-medium text-gray-900">Set up your alert</div>
                <div className="text-sm text-gray-600 pt-1">{getWorkflowTypeCopy(type)}</div>
              </div>
              <CustomizeSignalSection />
            </div>
          </div>
        </div>
      </div>
      <GeneralModal
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        title="Are you sure?"
        description="At least one of the columns you selected as a unique ID doesn't match a column returned in the preview query. Are you sure you have selected the right column names?"
      >
        <div className="flex justify-between w-full">
          <Button type="SECONDARY" onClick={() => setModalVisible(false)}>
            Go back
          </Button>
          <Button submit formHtmlId="MonitorEditFormCreatePage">
            Confirm and Save
          </Button>
        </div>
      </GeneralModal>
    </>
  );
}
